import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@giftsay/ui';
import { Bell, BellRing } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { describeOrderSignal, useOrderSignals } from '../hooks/use-order-signals.js';

function timeAgo(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function OrderNotificationBell() {
  const { enabled, recent, unread, markSeen } = useOrderSignals();
  const navigate = useNavigate();
  const location = useLocation();
  const [desktopPermission, setDesktopPermission] = React.useState(() =>
    typeof Notification === 'undefined' ? 'denied' : Notification.permission,
  );

  // Landing on the orders list counts as having seen what's new.
  React.useEffect(() => {
    if (location.pathname === '/orders' && unread > 0) markSeen();
  }, [location.pathname, unread, markSeen]);

  if (!enabled) return null;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        // Cleared on close, so the unread rows stay highlighted while the menu is open.
        if (!open) markSeen();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          aria-label={unread > 0 ? `${unread} new orders` : 'Order notifications'}
        >
          {unread > 0 ? <BellRing className="size-5" /> : <Bell className="size-5" />}
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] leading-none font-semibold text-destructive-foreground">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>
          {unread > 0 ? `${unread} new order${unread === 1 ? '' : 's'}` : 'Recent orders'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {recent.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            New orders will show up here.
          </div>
        ) : (
          recent.map((entry, index) => (
            <DropdownMenuItem
              key={entry.orderId}
              onClick={() => navigate(`/orders/${entry.orderId}`)}
              className="flex items-start gap-2"
            >
              <span
                className={`mt-1.5 size-2 shrink-0 rounded-full ${index < unread ? 'bg-primary' : 'bg-transparent'}`}
                aria-hidden="true"
              />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium">{entry.orderNumber}</span>
                <span className="text-xs text-muted-foreground">{describeOrderSignal(entry)}</span>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {timeAgo(entry.createdAt)}
              </span>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/orders')}>View all orders</DropdownMenuItem>
        {desktopPermission === 'default' && (
          <DropdownMenuItem
            onClick={() => {
              void Notification.requestPermission().then(setDesktopPermission);
            }}
          >
            Turn on desktop alerts
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
